import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchWellCompletionData,
  fetchCompletionWarningsData,
  WellCompletionData,
  deleteESPCompletions,
  deleteGLCompletions,
} from '../../services/well-completions/WellCompletionsService';

interface WellCompletionsState {
  currentCompletion: {
    data: WellCompletionData[] | null;
    loading: boolean;
    error: string | null;
  };

  completionWarnings: {
    data: any | null;
    loading: boolean;
    error: string | null;
  };

  selectedWellId: number | null;

  deleteESPCompletions: {
    data: undefined;
    loading: boolean;
    error: string | null;
  };

  deleteGLCompletions: {
    data: undefined;
    loading: boolean;
    error: string | null;
  };
}

const initialState: WellCompletionsState = {
  currentCompletion: {
    data: null,
    loading: false,
    error: null,
  },

  completionWarnings: {
    data: null,
    loading: false,
    error: null,
  },

  selectedWellId: null,

  deleteESPCompletions: {
    data: undefined,
    loading: false,
    error: null,
  },

  deleteGLCompletions: {
    data: undefined,
    loading: false,
    error: null,
  },
};

const WellCompletionsSlice = createSlice({
  name: 'wellCompletions',
  initialState,
  reducers: {
    setSelectedWellId: (state, action: PayloadAction<number>) => {
      state.selectedWellId = action.payload;
    },

    clearCompletionData: (state) => {
      state.currentCompletion = { data: null, loading: false, error: null };
      state.completionWarnings = { data: null, loading: false, error: null };
      state.selectedWellId = null;
      state.deleteESPCompletions = { data: undefined, loading: false, error: null };
      state.deleteGLCompletions = { data: undefined, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    // Fetch well completion data
    builder
      .addCase(fetchWellCompletionData.pending, (state) => {
        state.currentCompletion.loading = true;
        state.currentCompletion.error = null;
      })
      .addCase(fetchWellCompletionData.fulfilled, (state, action) => {
        state.currentCompletion.loading = false;
        state.currentCompletion.data = action.payload;
        state.currentCompletion.error = null;
      })
      .addCase(fetchWellCompletionData.rejected, (state, action) => {
        state.currentCompletion.loading = false;
        state.currentCompletion.error = action.error.message || 'Failed to fetch completion data';
        state.currentCompletion.data = null;
      });

    // Fetch completion warnings data
    builder
      .addCase(fetchCompletionWarningsData.pending, (state) => {
        state.completionWarnings.loading = true;
        state.completionWarnings.error = null;
      })
      .addCase(fetchCompletionWarningsData.fulfilled, (state, action) => {
        state.completionWarnings.loading = false;
        state.completionWarnings.data = action.payload;
        state.completionWarnings.error = null;
      })
      .addCase(fetchCompletionWarningsData.rejected, (state, action) => {
        state.completionWarnings.loading = false;
        state.completionWarnings.error = action.error.message || 'Failed to fetch completion warnings';
        state.completionWarnings.data = null;
      });

    // Delete ESP completions
    builder
      .addCase(deleteESPCompletions.pending, (state) => {
        state.deleteESPCompletions.loading = true;
        state.deleteESPCompletions.error = null;
      })
      .addCase(deleteESPCompletions.fulfilled, (state, action) => {
        state.deleteESPCompletions.loading = false;
        state.deleteESPCompletions.data = action.payload;
        state.deleteESPCompletions.error = null;
      })
      .addCase(deleteESPCompletions.rejected, (state, action) => {
        state.deleteESPCompletions.loading = false;
        state.deleteESPCompletions.error = action.error.message || 'Failed to delete ESP completions';
        state.deleteESPCompletions.data = undefined;
      });

    // Delete GL completions
    builder
      .addCase(deleteGLCompletions.pending, (state) => {
        state.deleteGLCompletions.loading = true;
        state.deleteGLCompletions.error = null;
      })
      .addCase(deleteGLCompletions.fulfilled, (state, action) => {
        state.deleteGLCompletions.loading = false;
        state.deleteGLCompletions.data = action.payload;
        state.deleteGLCompletions.error = null;
      })
      .addCase(deleteGLCompletions.rejected, (state, action) => {
        state.deleteGLCompletions.loading = false;
        state.deleteGLCompletions.error = action.error.message || 'Failed to delete GL completions';
        state.deleteGLCompletions.data = undefined;
      });
  },
});

export const { setSelectedWellId, clearCompletionData } = WellCompletionsSlice.actions;

export default WellCompletionsSlice.reducer;
